/**
 * Security utility functions for sanitizing and validating user input
 */

/**
 * Sanitizes and validates a redirect path to prevent XSS attacks.
 * Only allows safe, relative paths within the application.
 *
 * @param redirectPath - The path to validate
 * @returns A safe redirect path or null if invalid
 */
export const sanitizeRedirectPath = (redirectPath: string | null | undefined): string | null => {
    if (!redirectPath) {
        return null;
    }

    // Remove any leading/trailing whitespace
    const trimmed = redirectPath.trim();

    // Block dangerous URL schemes (javascript:, data:, vbscript:, etc.)
    const dangerousSchemes = /^(javascript|data|vbscript|file|about):/i;
    if (dangerousSchemes.test(trimmed)) {
        return null;
    }

    // Block absolute URLs (http://, https://, //)
    const absoluteUrlPattern = /^(https?:)?\/\//i;
    if (absoluteUrlPattern.test(trimmed)) {
        return null;
    }

    // Remove leading slashes to get the clean path
    const cleanPath = trimmed.replace(/^\/+/, '');

    // Block path traversal attempts (../, ..\, etc.)
    if (cleanPath.includes('..')) {
        return null;
    }

    // Block encoded special characters that could bypass filters
    const encodedDangerousChars = /%[0-9a-f]{2}/i;
    if (encodedDangerousChars.test(cleanPath)) {
        return null;
    }

    // Additional validation: only allow alphanumeric, hyphens, underscores, and forward slashes
    const safePathPattern = /^[a-zA-Z0-9\-_/]+$/;
    if (!safePathPattern.test(cleanPath)) {
        return null;
    }

    return cleanPath;
};

/**
 * Safely redirects to a relative path using window.location.replace with validation.
 * Falls back to a default path if the redirect path is invalid.
 *
 * @param redirectPath - The path to redirect to
 * @param fallbackPath - The fallback path if validation fails (default: 'eaton')
 */
export const safeRedirect = (redirectPath: string | null | undefined, fallbackPath: string = 'eaton'): void => {
    const sanitizedPath = sanitizeRedirectPath(redirectPath) || fallbackPath;

    try {
        const redirectUrl = new URL(`${window.location.origin}/${sanitizedPath}`);
        // Verify the origin matches (defense in depth)
        if (redirectUrl.origin === window.location.origin) {
            window.location.replace(redirectUrl.toString());
        } else {
            window.location.replace(`${window.location.origin}/${fallbackPath}`);
        }
    } catch (e) {
        window.location.replace(`${window.location.origin}/${fallbackPath}`);
    }
};
